import { useCallback, useMemo } from "react"
import { Wallet } from "lucide-react"
import { useGetBudgetAllocationMatrixQuery } from "shared/redux/endpoints/procurementV2"
import {
  useGetPaymentRequestBudgetPreviewQuery,
  useGetPaymentRequestBudgetAllocationsQuery,
  useLazyGetPaymentRequestBudgetAllocationsQuery,
} from "shared/redux/endpoints/procurementPayments"
import { useGetDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { Card } from "../../ui"
import { formatAmount } from "../../constants"
import BudgetAllocationCard from "./BudgetAllocationCard"
import { departmentList } from "./create/NonPoPaymentBuilder"
import { useNonPoBudgetPreview } from "./create/NonPoBudgetCheck"
import {
  fromMatrixRow,
  fromAllocationRow,
  fromDirectLines,
  fillValues,
  withLineValues,
  mergePeriodRows,
  downloadBudgetAllocationXlsx,
  VALIDATION_LABELS,
} from "./budgetAllocation"
import { HScroll } from "procurement/design/components/HScroll"

export const periodOf = (date) => {
  const d = date ? new Date(date) : new Date()
  const valid = !Number.isNaN(d.getTime()) ? d : new Date()
  return { month: valid.getMonth() + 1, year: valid.getFullYear() }
}

const mm = (month, year) => `${String(month).padStart(2, "0")}/${year}`

const useDepartmentNames = () => {
  const { data } = useGetDepartmentsQuery()
  return useMemo(
    () => new Map(departmentList(data).map((d) => [String(d.teamId ?? d.id), d.teamName || d.name || d.teamCode])),
    [data]
  )
}

// Create/edit form (PO-linked). `amountByPurchase` ({ purchaseId: amount
// being paid }) picks the POs to show; it and `poTotalByPurchase` must be
// memoized by the caller. Display fields come from the allocation matrix;
// "This payment" per line comes from `states` (usePoLineAllocations), typed
// through `editor`.
export const PoBudgetAllocation = ({ period, amountByPurchase, poTotalByPurchase, fileBase, states = {}, editor, disabled }) => {
  const { data, isFetching } = useGetBudgetAllocationMatrixQuery({ fiscalYear: period.year, fiscalMonth: period.month })
  const rows = useMemo(
    () =>
      mergePeriodRows(
        withLineValues(
          fillValues(
            (data || []).map(fromMatrixRow).filter((r) => r.purchaseId in amountByPurchase),
            amountByPurchase,
            poTotalByPurchase
          ),
          states
        )
      ),
    [data, amountByPurchase, poTotalByPurchase, states]
  )
  return (
    <BudgetAllocationCard
      rows={rows}
      period={period}
      fileBase={fileBase}
      loading={isFetching}
      editor={editor}
      disabled={disabled}
    />
  )
}

const impactText = (p) => {
  if (p.budgetImpact === "COMMITTED") return `Budget reserved in ${mm(p.fiscalMonth, p.fiscalYear)}`
  if (p.budgetImpact === "RELEASED") return "Budget released"
  return `Remaining after submit: ${formatAmount(p.remainingAfterPreview)}`
}

const NonPoBudgetImpact = ({ previews }) => (
  <Card icon={<Wallet aria-hidden />} title="Budget" subtitle={`${previews.length} center(s)`}>
    <HScroll className="pc-table-wrap">
      <table className="pc-table">
        <thead>
          <tr>
            <th>Center</th>
            <th>Category</th>
            <th className="pc-th-right">This payment</th>
            <th className="pc-th-right">Budget</th>
            <th className="pc-th-right">Committed</th>
            <th className="pc-th-right">Remaining</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {previews.map((p) => {
            const configIssue = p.checkStatus !== "BUDGET_FOUND" && p.checkStatus !== "INSUFFICIENT_BUDGET"
            return (
              <tr key={p.centerId}>
                <td className="pc-td-strong">{p.centerName || `Center #${p.centerId}`}</td>
                <td>{p.categoryName || "—"}</td>
                <td className="pc-td-right">{formatAmount(p.thisPayment)}</td>
                <td className="pc-td-right">{p.budget == null ? "—" : formatAmount(p.budget)}</td>
                <td className="pc-td-right">{p.committed == null ? "—" : formatAmount(p.committed)}</td>
                <td className="pc-td-right">{p.remaining == null ? "—" : formatAmount(p.remaining)}</td>
                <td style={{ color: configIssue || p.checkStatus === "INSUFFICIENT_BUDGET" ? "var(--pc-danger, #b42318)" : undefined }}>
                  {configIssue ? VALIDATION_LABELS[p.checkStatus] || p.checkStatus : impactText(p)}
                  {p.checkStatus === "INSUFFICIENT_BUDGET" && <div>{VALIDATION_LABELS.INSUFFICIENT_BUDGET} — submit will be rejected.</div>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </HScroll>
  </Card>
)

// Saved PR (detail page), PO-linked or Non-PO. Only a Non-PO has a budget
// preview (the endpoint answers 409 for a PR with a PO).
export const PaymentRequestBudgetAllocation = ({ pr }) => {
  const { data: previews } = useGetPaymentRequestBudgetPreviewQuery(pr.id, { skip: !pr.direct })
  const { data, isFetching } = useGetPaymentRequestBudgetAllocationsQuery(pr.id)
  const departmentNames = useDepartmentNames()
  const rows = useMemo(
    () => mergePeriodRows((data || []).map((r) => fromAllocationRow(r, departmentNames))),
    [data, departmentNames]
  )
  const period = rows[0]?.month ? { month: rows[0].month, year: rows[0].year } : periodOf(pr.createdAt)
  return (
    <>
      {pr.direct && previews?.length > 0 && <NonPoBudgetImpact previews={previews} />}
      <BudgetAllocationCard rows={rows} period={period} fileBase={pr.paymentCode || "payment-request"} loading={isFetching} />
    </>
  )
}

// Non-PO before it is saved: no PO/OR exists yet, so rows come from the
// builder's own lines (`value` = emptyNonPoSpend shape).
export const DirectBudgetAllocation = ({ period, value, fileBase }) => {
  const { byCenter, pending } = useNonPoBudgetPreview(value.lines, value.budgetCategoryId)
  const departmentNames = useDepartmentNames()

  const rows = useMemo(
    () =>
      fromDirectLines({
        lines: value.lines.filter((l) => l.centerId),
        previewByCenter: byCenter,
        departmentName: departmentNames.get(String(value.departmentId)),
        month: period.month,
        year: period.year,
      }),
    [value, departmentNames, byCenter, period]
  )

  return <BudgetAllocationCard rows={rows} period={period} fileBase={fileBase} loading={pending && rows.length === 0} />
}

// For the PR list: downloads the same rows as the detail card. Resolves to
// the number of rows exported.
export const useDownloadPaymentRequestAllocation = () => {
  const [getAllocations] = useLazyGetPaymentRequestBudgetAllocationsQuery()
  const departmentNames = useDepartmentNames()
  return useCallback(
    async (id, paymentCode) => {
      const data = await getAllocations(id, true).unwrap()
      const rows = data.map((r) => fromAllocationRow(r, departmentNames))
      if (rows.length) await downloadBudgetAllocationXlsx(rows, paymentCode || "payment-request")
      return rows.length
    },
    [getAllocations, departmentNames]
  )
}
